import { fetchAuthSession, signInWithRedirect } from 'aws-amplify/auth';

/**
 * Start the Okta OAuth redirect, but only if nobody is signed in yet.
 *
 * App.tsx used to call `signInWithRedirect` unconditionally on every load whenever
 * `oktaAutoRedirect` was enabled. For an ALREADY-authenticated user Amplify then threw
 * `UserAlreadyAuthenticatedException: There is already a signed in user`, which was caught and
 * logged as "Failed to redirect to Okta OAuth" — on every single page load.
 *
 * That log line was wrong twice over: nothing failed, and there was nothing to redirect. A
 * permanent red error in the console is not free — it trains everyone to ignore the console, so a
 * real auth failure reads as background noise. (It is also what made validating THUB-299 on DEV
 * noisier than it needed to be: every console check had to first rule this one out.)
 *
 * `fetchAuthSession` is the session check already used elsewhere in this codebase
 * (contexts/WebSocketContext.tsx, services/api/base.ts), so it is the one used here rather than
 * introducing `getCurrentUser` alongside it.
 */
export type OktaRedirectOutcome = 'already-signed-in' | 'redirecting' | 'failed';

export async function redirectToOktaIfSignedOut(): Promise<OktaRedirectOutcome> {
  // A signed-in user needs no redirect. fetchAuthSession resolves with no tokens when signed out,
  // and can also reject outright — both mean "not signed in", so both fall through to the redirect.
  try {
    const session = await fetchAuthSession();
    if (session.tokens?.idToken) {
      return 'already-signed-in';
    }
  } catch {
    // Not signed in (or no session to read) — carry on to the redirect.
  }

  try {
    await signInWithRedirect({ provider: { custom: 'Okta' } });
    return 'redirecting';
  } catch (err) {
    // Still guard the race: a session can be established between the check above and this call
    // (a concurrent tab completing its own sign-in). That is benign, not a failure to report.
    if (err instanceof Error && err.name === 'UserAlreadyAuthenticatedException') {
      return 'already-signed-in';
    }
    // A genuine redirect failure DOES deserve the console — this is the case the old catch was
    // presumably written for, and it stays loud.
    console.error('Failed to redirect to Okta OAuth:', err);
    return 'failed';
  }
}
